import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { ArticleResume } from "@modules/article/components/articleResume"
import { Article } from "@modules/article/entities/articleEntity"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { Suspense, useMemo } from "react"
import { ErrorBoundary } from "react-error-boundary"

type ArticleCardProps = {
  article: Article
} & React.ComponentProps<"div">

export function ArticleCard({ article, className, ...props }: ArticleCardProps) {
  const { sidebarNavigateTo } = useSidebar()

  const itemImage = useMemo(() => {
    return article.thumbnail.uri
  }, [article])

  const onOpenArticle = () => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ArticleResume article={article} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })
  }

  return (
    <div
      className={cn(
        "group relative aspect-square w-full max-w-80 overflow-hidden rounded-[33px] bg-cover bg-center bg-no-repeat hover:cursor-pointer lg:max-w-97",
        className,
      )}
      style={{
        backgroundImage: `url(${itemImage})`,
      }}
      onClick={() => onOpenArticle()}
      {...props}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-1 rounded-4xl bg-linear-to-b from-[rgba(34,34,34,0.4)] to-[#222]" />

      {/* Category Tag */}
      <div className="absolute top-[10px] right-5 z-2 flex min-w-[80px] justify-center rounded-[44px] bg-white/10 px-3 py-2 backdrop-blur-[7px]">
        <Text fontStyle="LL" className="text-textOnColor-body">
          {word(`article.tag.${article.tag}`)}
        </Text>
      </div>

      {/* Description Text */}
      <div className="absolute bottom-5 left-5 z-2 flex w-[calc(100%-40px)] flex-col gap-1.5">
        <Text fontStyle="TL" className="text-textOnColor-body">
          {article.title}
        </Text>
        <Text fontStyle="BS" className="text-textOnColor-body">
          {article.description}
        </Text>
      </div>
    </div>
  )
}
